import { useState, useEffect, useCallback } from 'react'
import { syncAdminAPI } from '../../services/api'

// ==========================================
// STATUS BADGE
// ==========================================
function StatusBadge({ status, isOnline }) {
  const getStyle = () => {
    if (status === 'PENDIENTE_AUTORIZACION') return 'bg-yellow-100 text-yellow-800'
    if (status === 'RECHAZADO') return 'bg-red-100 text-red-800'
    if (status === 'SUSPENDIDO') return 'bg-gray-100 text-gray-600'
    if (isOnline === false) return 'bg-gray-100 text-gray-600'
    // ACTIVO
    if (status === 'ERROR') return 'bg-red-100 text-red-800'
    return 'bg-green-100 text-green-800'
  }

  const getLabel = () => {
    if (status === 'PENDIENTE_AUTORIZACION') return 'Pendiente'
    if (status === 'RECHAZADO') return 'Rechazado'
    if (status === 'SUSPENDIDO') return 'Suspendido'
    if (isOnline === false) return 'Offline'
    return 'Online'
  }

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStyle()}`}>
      <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
        isOnline && status === 'ACTIVO' ? 'bg-green-500 animate-pulse' :
        status === 'PENDIENTE_AUTORIZACION' ? 'bg-yellow-500' :
        'bg-gray-400'
      }`} />
      {getLabel()}
    </span>
  )
}

// ==========================================
// SUMMARY CARDS
// ==========================================
function SummaryCards({ summary }) {
  const cards = [
    { label: 'Total', value: summary.totalInstances, color: 'text-gray-900', bg: 'bg-white' },
    { label: 'Online', value: summary.online, color: 'text-green-700', bg: 'bg-green-50' },
    { label: 'Offline', value: summary.offline, color: 'text-gray-600', bg: 'bg-gray-50' },
    { label: 'Errores', value: summary.withErrors, color: 'text-red-700', bg: 'bg-red-50' },
    { label: 'Pendientes', value: summary.pendingAuth, color: 'text-yellow-700', bg: 'bg-yellow-50' },
  ]

  return (
    <div className="grid grid-cols-5 gap-3">
      {cards.map(c => (
        <div key={c.label} className={`${c.bg} rounded-lg p-3 border border-gray-200`}>
          <p className="text-xs text-gray-500 uppercase tracking-wide">{c.label}</p>
          <p className={`text-2xl font-bold ${c.color}`}>{c.value}</p>
        </div>
      ))}
    </div>
  )
}

// ==========================================
// AUTHORIZATION QUEUE
// ==========================================
function AuthorizationQueue({ authorizations, onApprove, onReject, loading }) {
  const [rejectingId, setRejectingId] = useState(null)
  const [rejectReason, setRejectReason] = useState('')

  if (authorizations.length === 0) return null

  const handleReject = (id) => {
    onReject(id, rejectReason)
    setRejectingId(null)
    setRejectReason('')
  }

  return (
    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
      <h3 className="text-sm font-semibold text-yellow-800 mb-3">
        Instalaciones pendientes de autorizacion ({authorizations.length})
      </h3>
      <div className="space-y-2">
        {authorizations.map(auth => (
          <div key={auth.installation_id} className="bg-white rounded-lg p-3 border border-yellow-100 flex items-center justify-between">
            <div>
              <span className="font-medium text-sm">Local {auth.numero_local}</span>
              <span className="text-xs text-gray-500 ml-2">Tenant: {auth.tenant_id}</span>
              {auth.hostname && <span className="text-xs text-gray-400 ml-2">({auth.hostname})</span>}
              {auth.ip_address && <span className="text-xs text-gray-400 ml-1">- {auth.ip_address}</span>}
            </div>
            <div className="flex items-center gap-2">
              {rejectingId === auth.installation_id ? (
                <>
                  <input
                    type="text"
                    placeholder="Razon (opcional)"
                    value={rejectReason}
                    onChange={e => setRejectReason(e.target.value)}
                    className="text-xs border rounded px-2 py-1 w-40"
                  />
                  <button
                    onClick={() => handleReject(auth.installation_id)}
                    disabled={loading}
                    className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50"
                  >
                    Confirmar
                  </button>
                  <button
                    onClick={() => { setRejectingId(null); setRejectReason('') }}
                    className="px-2 py-1 text-xs text-gray-500 hover:text-gray-700"
                  >
                    Cancelar
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => onApprove(auth.installation_id)}
                    disabled={loading}
                    className="px-3 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-50"
                  >
                    Aprobar
                  </button>
                  <button
                    onClick={() => setRejectingId(auth.installation_id)}
                    disabled={loading}
                    className="px-3 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200 disabled:opacity-50"
                  >
                    Rechazar
                  </button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ==========================================
// INSTANCE LIST
// ==========================================
function InstanceList({ instances, selectedId, onSelect }) {
  return (
    <div className="space-y-1">
      {instances.map(inst => (
        <button
          key={inst.installation_id}
          onClick={() => onSelect(inst)}
          className={`w-full text-left px-3 py-2.5 rounded-lg transition text-sm ${
            selectedId === inst.installation_id
              ? 'bg-primary-50 border border-primary-200'
              : 'hover:bg-gray-50 border border-transparent'
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="font-medium">Local {inst.numero_local}</span>
            <StatusBadge status={inst.estado} isOnline={inst.is_online} />
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-xs text-gray-500">{inst.heartbeat_hostname || inst.hostname || '-'}</span>
            {inst.ip_address && <span className="text-xs text-gray-400">{inst.ip_address}</span>}
          </div>
          {inst.last_heartbeat_at && (
            <p className="text-[10px] text-gray-400 mt-0.5">
              Ultimo: {formatTimeAgo(inst.last_heartbeat_at)}
            </p>
          )}
        </button>
      ))}
    </div>
  )
}

// ==========================================
// INSTANCE DETAIL
// ==========================================
function InstanceDetail({ instance, commands, onSendCommand, commandLoading }) {
  if (!instance) {
    return (
      <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">
        Selecciona una instancia para ver detalles
      </div>
    )
  }

  const inst = instance

  return (
    <div className="flex-1 overflow-y-auto p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Local {inst.numero_local}</h2>
          <p className="text-xs text-gray-500 font-mono">{inst.installation_id}</p>
        </div>
        <StatusBadge status={inst.estado} isOnline={inst.is_online} />
      </div>

      {/* System Info */}
      <div className="bg-gray-50 rounded-lg p-3 space-y-1.5 text-sm">
        <h3 className="text-xs font-semibold text-gray-500 uppercase">Sistema</h3>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1">
          <InfoRow label="Hostname" value={inst.heartbeat_hostname || inst.hostname} />
          <InfoRow label="IP" value={inst.ip_address} />
          <InfoRow label="OS" value={inst.os_info} />
          <InfoRow label="Version" value={inst.agent_version} />
          <InfoRow label="Uptime" value={inst.uptime_seconds ? formatUptime(inst.uptime_seconds) : '-'} />
          <InfoRow label="Memoria" value={inst.memory_usage_mb ? `${inst.memory_usage_mb} MB` : '-'} />
        </div>
      </div>

      {/* Sync Stats */}
      <div className="bg-gray-50 rounded-lg p-3 space-y-1.5 text-sm">
        <h3 className="text-xs font-semibold text-gray-500 uppercase">Sincronizacion</h3>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1">
          <InfoRow label="Actividad" value={inst.current_activity || 'Idle'} />
          <InfoRow label="Total syncs" value={inst.total_syncs_session} />
          <InfoRow label="Ultimo inicio" value={inst.last_sync_started_at ? formatDate(inst.last_sync_started_at) : '-'} />
          <InfoRow label="Ultimo fin" value={inst.last_sync_completed_at ? formatDate(inst.last_sync_completed_at) : '-'} />
          <InfoRow label="Registros" value={inst.last_sync_records ?? '-'} />
          <InfoRow label="Errores" value={inst.last_sync_errors ?? '-'} />
        </div>
      </div>

      {/* Config Params */}
      {inst.config_params && Object.keys(inst.config_params).length > 0 && (
        <div className="bg-gray-50 rounded-lg p-3 space-y-1.5 text-sm">
          <h3 className="text-xs font-semibold text-gray-500 uppercase">Config.ini</h3>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1">
            {Object.entries(inst.config_params).map(([key, val]) => (
              <InfoRow key={key} label={key} value={val} />
            ))}
          </div>
        </div>
      )}

      {/* Command Buttons */}
      {inst.estado === 'ACTIVO' && (
        <div className="space-y-2">
          <h3 className="text-xs font-semibold text-gray-500 uppercase">Comandos remotos</h3>
          <div className="flex flex-wrap gap-2">
            <CommandButton label="Pausar" command="PAUSE" onClick={onSendCommand} loading={commandLoading} color="yellow" />
            <CommandButton label="Reanudar" command="RESUME" onClick={onSendCommand} loading={commandLoading} color="green" />
            <CommandButton label="Sync ahora" command="SYNC_NOW" onClick={onSendCommand} loading={commandLoading} color="blue" />
            <CommandButton label="Reiniciar" command="RESTART" onClick={onSendCommand} loading={commandLoading} color="red" />
          </div>
        </div>
      )}

      {/* Command History */}
      {commands.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-xs font-semibold text-gray-500 uppercase">Historial de comandos</h3>
          <div className="space-y-1 max-h-48 overflow-y-auto">
            {commands.map(cmd => (
              <div key={cmd.id} className="flex items-center justify-between text-xs bg-white border rounded px-2 py-1.5">
                <div className="flex items-center gap-2">
                  <span className="font-mono font-medium">{cmd.command}</span>
                  <CommandStatusBadge status={cmd.status} />
                </div>
                <span className="text-gray-400">{formatDate(cmd.created_at)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ==========================================
// SMALL COMPONENTS
// ==========================================

function InfoRow({ label, value }) {
  return (
    <div className="flex justify-between">
      <span className="text-gray-500">{label}</span>
      <span className="text-gray-900 font-medium text-right truncate max-w-[60%]">{value ?? '-'}</span>
    </div>
  )
}

function CommandButton({ label, command, onClick, loading, color }) {
  const colorMap = {
    yellow: 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200',
    green: 'bg-green-100 text-green-800 hover:bg-green-200',
    blue: 'bg-blue-100 text-blue-800 hover:bg-blue-200',
    red: 'bg-red-100 text-red-800 hover:bg-red-200',
  }

  return (
    <button
      onClick={() => onClick(command)}
      disabled={loading}
      className={`px-3 py-1.5 text-xs font-medium rounded-lg transition disabled:opacity-50 ${colorMap[color]}`}
    >
      {label}
    </button>
  )
}

function CommandStatusBadge({ status }) {
  const styles = {
    PENDING: 'bg-yellow-100 text-yellow-700',
    ACKNOWLEDGED: 'bg-blue-100 text-blue-700',
    EXECUTING: 'bg-blue-100 text-blue-700',
    EXECUTED: 'bg-green-100 text-green-700',
    FAILED: 'bg-red-100 text-red-700',
    EXPIRED: 'bg-gray-100 text-gray-500',
    CANCELLED: 'bg-gray-100 text-gray-500',
  }

  return (
    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${styles[status] || 'bg-gray-100 text-gray-500'}`}>
      {status}
    </span>
  )
}

// ==========================================
// HELPERS
// ==========================================

function formatTimeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'hace segundos'
  if (mins < 60) return `hace ${mins}m`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `hace ${hours}h`
  return `hace ${Math.floor(hours / 24)}d`
}

function formatUptime(seconds) {
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  return h > 0 ? `${h}h ${m}m` : `${m}m`
}

function formatDate(dateStr) {
  if (!dateStr) return '-'
  const d = new Date(dateStr)
  return d.toLocaleString('es-CL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
}

// ==========================================
// MAIN VIEW
// ==========================================

export default function SyncAdminView() {
  const [summary, setSummary] = useState({ totalInstances: 0, online: 0, offline: 0, withErrors: 0, pendingAuth: 0 })
  const [instances, setInstances] = useState([])
  const [authorizations, setAuthorizations] = useState([])
  const [selectedInstance, setSelectedInstance] = useState(null)
  const [commands, setCommands] = useState([])
  const [loading, setLoading] = useState(true)
  const [commandLoading, setCommandLoading] = useState(false)
  const [authLoading, setAuthLoading] = useState(false)

  // Fetch all data
  const fetchData = useCallback(async () => {
    try {
      const [summaryRes, instancesRes, authRes] = await Promise.all([
        syncAdminAPI.getSummary(),
        syncAdminAPI.getInstances(),
        syncAdminAPI.getAuthorizations(),
      ])
      setSummary(summaryRes.data)
      setInstances(instancesRes.data)
      setAuthorizations(authRes.data)
    } catch (error) {
      console.error('Error fetching sync admin data:', error)
    } finally {
      setLoading(false)
    }
  }, [])

  // Initial load + polling every 30s
  useEffect(() => {
    fetchData()
    const interval = setInterval(fetchData, 30000)
    return () => clearInterval(interval)
  }, [fetchData])

  // Fetch commands when selecting an instance
  useEffect(() => {
    if (!selectedInstance) {
      setCommands([])
      return
    }
    syncAdminAPI.getInstanceCommands(selectedInstance.installation_id)
      .then(res => setCommands(res.data))
      .catch(() => setCommands([]))
  }, [selectedInstance?.installation_id])

  // Handlers
  const handleSelect = (inst) => {
    setSelectedInstance(inst)
  }

  const handleSendCommand = async (command) => {
    if (!selectedInstance) return
    setCommandLoading(true)
    try {
      await syncAdminAPI.sendCommand(selectedInstance.installation_id, command)
      // Refresh commands
      const res = await syncAdminAPI.getInstanceCommands(selectedInstance.installation_id)
      setCommands(res.data)
    } catch (error) {
      console.error('Error sending command:', error)
    } finally {
      setCommandLoading(false)
    }
  }

  const handleApprove = async (id) => {
    setAuthLoading(true)
    try {
      await syncAdminAPI.approve(id)
      await fetchData()
    } catch (error) {
      console.error('Error approving:', error)
    } finally {
      setAuthLoading(false)
    }
  }

  const handleReject = async (id, reason) => {
    setAuthLoading(true)
    try {
      await syncAdminAPI.reject(id, reason)
      await fetchData()
    } catch (error) {
      console.error('Error rejecting:', error)
    } finally {
      setAuthLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-gray-400 text-sm">Cargando panel de sincronizacion...</div>
      </div>
    )
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Top: Summary + Auth Queue */}
      <div className="p-4 space-y-3 border-b border-gray-200 bg-white">
        <div className="flex items-center justify-between">
          <h1 className="text-lg font-semibold text-gray-900">LibertadSync - Panel de Administracion</h1>
          <span className="text-xs text-gray-400">Auto-refresh: 30s</span>
        </div>
        <SummaryCards summary={summary} />
        <AuthorizationQueue
          authorizations={authorizations}
          onApprove={handleApprove}
          onReject={handleReject}
          loading={authLoading}
        />
      </div>

      {/* Bottom: Instance list + Detail */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left: Instance list */}
        <div className="w-72 border-r border-gray-200 bg-white overflow-y-auto p-3">
          <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">
            Instancias ({instances.length})
          </h3>
          {instances.length === 0 ? (
            <p className="text-xs text-gray-400 py-4 text-center">No hay instancias registradas</p>
          ) : (
            <InstanceList
              instances={instances}
              selectedId={selectedInstance?.installation_id}
              onSelect={handleSelect}
            />
          )}
        </div>

        {/* Right: Detail */}
        <InstanceDetail
          instance={selectedInstance}
          commands={commands}
          onSendCommand={handleSendCommand}
          commandLoading={commandLoading}
        />
      </div>
    </div>
  )
}
